// react
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from "react-router-dom";
import * as Cookies from "js-cookie";

// @material-ui/icons
import Announcement from "@material-ui/icons/Announcement";
import AttachFile from "@material-ui/icons/AttachFile";
import Check from "@material-ui/icons/Check";

// @material-ui components
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";

// core components
import Button from "beaktShared/components/CustomButtons/Button.js";
import Card from "beaktShared/components/Card/Card.js";
import CardBody from "beaktShared/components/Card/CardBody.js";
import CardIcon from "beaktShared/components/Card/CardIcon.js";
import CardHeader from "beaktShared/components/Card/CardHeader.js";
import SnackbarContent from "beaktShared/components/Snackbar/SnackbarContent.js";
import ComunicadoConteudo from "beaktShared/components/TipTap/ComunicadoConteudo";
import TipoComunicadoBadge from "./TipoComunicadoBadge";

// services
import * as ComunicadoPendenteService from "./ComunicadoPendenteService";
import { EVENTO_COMUNICADO_PENDENTE } from "beaktShared/services/crudeApiService";
import { formateDate } from "beaktShared/helpers/masks";

// styles
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/regularFormsStyle";

const useStyles = makeStyles({
    ...styles,
    paper: {
        backgroundColor: "#eeeeee"
    },
    container: {
        maxWidth: "960px",
        margin: "0 auto",
        width: "100%"
    },
    aviso: {
        textAlign: "center",
        color: "#555",
        margin: "8px 0 16px"
    },
    info: {
        color: "#777",
        fontSize: "13px",
        marginBottom: "12px",
        "& > span": {
            marginRight: "16px"
        }
    },
    conteudo: {
        border: "1px solid #e0e0e0",
        borderRadius: "5px",
        padding: "16px",
        marginBottom: "16px",
        maxHeight: "50vh",
        overflowY: "auto",
        backgroundColor: "#fff"
    },
    anexos: {
        marginBottom: "16px",
        "& > *": {
            margin: "0 8px 8px 0"
        }
    },
    rodape: {
        textAlign: "center",
        marginTop: "8px"
    }
});

// Intervalo minimo entre consultas disparadas por troca de rota (novos comunicados publicados durante a sessao)
const INTERVALO_MINIMO_CONSULTA_MS = 60 * 1000;

// verificando: aguardando a 1a resposta (ou nova tentativa) - tela coberta
// bloqueado: ha comunicado sem aceite | erro: nao foi possivel confirmar - tela coberta (fail-closed)
// liberado: a API confirmou que nao ha pendencias
const STATUS = {
    verificando: "verificando",
    bloqueado: "bloqueado",
    erro: "erro",
    liberado: "liberado",
};

const mainAPP = process.env.REACT_APP_MAIN;

// Bloqueio global pos-login: cobre toda a tela desde a montagem ate a API confirmar que nao ha comunicado sem aceite.
// Nao existe fechar, ESC, clique fora ou "ver depois". Em falha da consulta a tela continua bloqueada (fail-closed)
// com "Tentar novamente" e "Sair". O backend tambem bloqueia (409 COMUNICADO_PENDENTE em todos os endpoints de negocio);
// esse 409 dispara EVENTO_COMUNICADO_PENDENTE, que forca nova consulta aqui.
// As pendencias sao do usuario em todas as suas unidades (o aceite e um por usuario), igual a checagem do backend.
// Props: history (router do layout),
// onPendentesChange(total) opcional - informa ao layout a quantidade de pendentes (badge do menu)
export default function ComunicadoBlockOverlay({ history, onPendentesChange }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const location = useLocation();

    const [status, setStatus] = useState(STATUS.verificando);
    const [pendentes, setPendentes] = useState([]);
    const [verificando, setVerificando] = useState(false);
    const [concordo, setConcordo] = useState(false);
    const [isConfirmando, setIsConfirmando] = useState(false);
    const [erroAceite, setErroAceite] = useState("");

    const statusRef = useRef(STATUS.verificando);
    const ultimaConsultaRef = useRef(0);
    const consultandoRef = useRef(false);
    const montadoRef = useRef(true);
    // A tela ficou bloqueada e a API recusou requisicoes (409): ao liberar, a pagina e recarregada,
    // pois o que estava por tras do overlay foi carregado sem dados
    const requisicaoRecusadaRef = useRef(false);

    const alterarStatus = (novoStatus) => {
        statusRef.current = novoStatus;
        setStatus(novoStatus);
    };

    useEffect(() => {
        montadoRef.current = true;
        return () => { montadoRef.current = false; };
    }, []);

    const consultarPendentes = (forcar) => {
        if (consultandoRef.current)
            return Promise.resolve();
        if (!forcar && Date.now() - ultimaConsultaRef.current < INTERVALO_MINIMO_CONSULTA_MS)
            return Promise.resolve();

        consultandoRef.current = true;
        ultimaConsultaRef.current = Date.now();
        const estavaBloqueado = statusRef.current === STATUS.bloqueado;

        return ComunicadoPendenteService.listarPendentes().then(result => {
            consultandoRef.current = false;
            if (!montadoRef.current)
                return;

            // Fail-closed: sem resposta valida da API a tela continua bloqueada ate uma nova tentativa confirmar
            if (!result.ok) {
                ultimaConsultaRef.current = 0;
                alterarStatus(STATUS.erro);
                return;
            }

            const lista = result.data || [];
            setPendentes(lista);

            if (lista.length > 0) {
                alterarStatus(STATUS.bloqueado);
                return;
            }

            // Somente depois do ultimo aceite (estava bloqueado), para nunca recarregar em loop
            if (estavaBloqueado && requisicaoRecusadaRef.current) {
                window.location.reload();
                return;
            }

            alterarStatus(STATUS.liberado);
        });
    };

    // 1a consulta ao montar o layout; depois, a cada troca de rota respeitando o intervalo minimo.
    // Com a tela coberta nao ha navegacao possivel, entao so consulta de novo quando liberado.
    useEffect(() => {
        consultarPendentes(true);
    // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (statusRef.current === STATUS.liberado)
            consultarPendentes(false);
    // eslint-disable-next-line
    }, [location.pathname]);

    // A API recusou uma requisicao por comunicado pendente (publicado durante a sessao, ou a tela por tras do overlay)
    useEffect(() => {
        const handleComunicadoPendente = () => {
            requisicaoRecusadaRef.current = true;
            if (statusRef.current === STATUS.liberado || statusRef.current === STATUS.erro)
                consultarPendentes(true);
        };
        window.addEventListener(EVENTO_COMUNICADO_PENDENTE, handleComunicadoPendente);
        return () => window.removeEventListener(EVENTO_COMUNICADO_PENDENTE, handleComunicadoPendente);
    // eslint-disable-next-line
    }, []);

    const bloqueado = status === STATUS.bloqueado && pendentes.length > 0;

    const totalPendentes = status === STATUS.liberado ? 0 : pendentes.length;
    useEffect(() => {
        if (onPendentesChange)
            onPendentesChange(totalPendentes);
    // eslint-disable-next-line
    }, [totalPendentes]);

    const atual = bloqueado ? pendentes[0] : null;
    const comunicadoAtualId = atual ? atual.comunicadoId : null;

    // Cada comunicado exige a sua propria marcacao de "li e concordo"
    useEffect(() => {
        setConcordo(false);
        setErroAceite("");
    }, [comunicadoAtualId]);

    const handleConfirmarLeitura = () => {
        if (!atual || !concordo || isConfirmando)
            return;

        setIsConfirmando(true);
        setErroAceite("");
        // Sem unidade: o servidor registra o aceite na unidade do usuario alcancada pelo comunicado
        ComunicadoPendenteService.aceitar(atual.comunicadoId).then(result => {
            if (!montadoRef.current)
                return;
            setIsConfirmando(false);
            if (!result.ok) {
                setErroAceite(result.message || t('notice_board.acceptance_error'));
                return;
            }

            const restantes = pendentes.filter(c => c.comunicadoId !== atual.comunicadoId);
            if (restantes.length > 0) {
                setPendentes(restantes);
                return;
            }

            // Ultimo aceite: confirma na API antes de liberar (pode ter sido publicado outro comunicado)
            setVerificando(true);
            consultarPendentes(true).then(() => {
                if (montadoRef.current)
                    setVerificando(false);
            });
        });
    };

    const handleSair = () => {
        Cookies.remove("session");
        localStorage.removeItem('carrinhoVenda');
        // Mesmo fluxo de logout do AdminNavbarLinks; o overlay nao e fechado, o layout e desmontado pela navegacao
        if (mainAPP === 'DAY')
            history.push("/institutionalday/login");
        else
            history.push("/auth/login");
    };

    const handleTentarNovamente = () => {
        alterarStatus(STATUS.verificando);
        consultarPendentes(true);
    };

    if (status === STATUS.liberado)
        return null;

    // Verificando ou falha na consulta: tela coberta sem conteudo do sistema ate a API confirmar
    if (!bloqueado) {
        const comErro = status === STATUS.erro;
        return (
            <Dialog
                open={true}
                fullScreen
                disableEscapeKeyDown
                disableBackdropClick
                classes={{ paper: classes.paper }}
                aria-busy={!comErro}
            >
                <DialogContent>
                    <div className={classes.container}>
                        <Card>
                            <CardBody>
                                <div className={classes.rodape}>
                                    {comErro
                                        ? <SnackbarContent color="danger" message={t('notice_board.check_error')} />
                                        : <p className={classes.aviso}><CircularProgress size={16} color="inherit" /> {t('notice_board.checking_notices')}</p>}
                                    {comErro &&
                                        <Button color="primary" onClick={handleTentarNovamente}>
                                            {t('notice_board.try_again')}
                                        </Button>}
                                </div>
                            </CardBody>
                        </Card>

                        <div className={classes.rodape}>
                            <Button color="transparent" size="sm" onClick={handleSair}>
                                {t('sidebar.logout')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={true}
            fullScreen
            disableEscapeKeyDown
            disableBackdropClick
            classes={{ paper: classes.paper }}
            aria-labelledby="comunicado-bloqueio-titulo"
        >
            <DialogContent>
                <div className={classes.container}>
                    <p className={classes.aviso}>
                        {t('notice_board.block_message')}
                        {pendentes.length > 1 &&
                            <b> {t('notice_board.block_counter', { current: 1, total: pendentes.length })}</b>}
                    </p>

                    <Card>
                        <CardHeader color="primary" icon>
                            <CardIcon color="primary">
                                <Announcement />
                            </CardIcon>
                            <h4 className={classes.cardIconTitle} id="comunicado-bloqueio-titulo">{atual.titulo}</h4>
                        </CardHeader>
                        <CardBody>
                            <div className={classes.info}>
                                <span><TipoComunicadoBadge tipo={atual.tipo} /></span>
                                <span>{t('notice_board.published')}: {formateDate(atual.dataCriacao)}</span>
                                {atual.dataLimiteAceite &&
                                    <span>{t('notice_board.deadline')}: {formateDate(atual.dataLimiteAceite)}</span>}
                            </div>

                            <div className={classes.conteudo}>
                                <ComunicadoConteudo html={atual.descricao} />
                            </div>

                            {atual.anexos && atual.anexos.length > 0 &&
                                <div className={classes.anexos}>
                                    {atual.anexos.map(anexo => (
                                        <Chip
                                            key={anexo.comunicadoAnexoId}
                                            variant="outlined"
                                            color="primary"
                                            icon={<AttachFile />}
                                            label={anexo.nomeArquivo}
                                            clickable
                                            onClick={() => window.open(anexo.url, "_blank", "noopener,noreferrer")}
                                        />
                                    ))}
                                </div>}

                            {atual.dataLimiteAceite &&
                                <SnackbarContent
                                    color="warning"
                                    message={t('notice_board.deadline_inactivation_warning', { date: formateDate(atual.dataLimiteAceite) })}
                                />}

                            {erroAceite &&
                                <SnackbarContent color="danger" message={erroAceite} />}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        tabIndex={-1}
                                        checked={concordo}
                                        disabled={isConfirmando || verificando}
                                        onChange={e => setConcordo(e.target.checked)}
                                        checkedIcon={<Check className={classes.checkedIcon} />}
                                        icon={<Check className={classes.uncheckedIcon} />}
                                        classes={{ checked: classes.checked, root: classes.checkRoot }}
                                    />
                                }
                                classes={{ label: classes.label, root: classes.labelRoot }}
                                label={t('notice_board.agree_checkbox')}
                            />
                            <div className={classes.textCenter}>
                                <Button color="primary" disabled={!concordo || isConfirmando || verificando} onClick={handleConfirmarLeitura}>
                                    {isConfirmando || verificando
                                        ? <><CircularProgress size={16} color="inherit" /> {t('label.loading')}...</>
                                        : t('notice_board.confirm_reading')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>

                    <div className={classes.rodape}>
                        <Button color="transparent" size="sm" disabled={isConfirmando || verificando} onClick={handleSair}>
                            {t('sidebar.logout')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
